/**
 * TicketType — a sellable line under an event: a real ticket or an add-on,
 * chained into a visibility chain via `showAfterSoldOut` rather than priced
 * through tiers.
 *
 * Path: `workspaces/{orgId}/events/{eventId}/ticketTypes/{typeId}` — public
 * read; member create/update behind `tickets.events.manage`, denied until
 * `mfa: true`.
 *
 * `delete` is server-only behind `deleteTicketType`: the guard fact is whether
 * any ticket still names this type or its `quota/inventory` has consumed
 * seats — `OrderLine.typeId` lives inside an array of maps and is not
 * queryable at all — so the door probes both inside the deleting transaction
 * and refuses with counts. The kernel rule keeps the manage + mfa predicate —
 * a vacuous one would answer `me.can('delete', TicketType)` true for any
 * authenticated actor — and `.requiresHandler` is what suppresses its emission.
 *
 * `Cents`, `PositiveCents` and `Qty` are the shared money/quantity primitives
 * every entity in this package prices and counts with.
 */

import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import { LocalizedTextSchema } from "@momo/i18n";
import type { Timestamp } from "firebase/firestore";
import { z } from "zod";
import type { Availability, Event } from "./event.entity.ts";
import { LocalizedCopyStatusSchema } from "./localized-copy-status.const.ts";
import {
	instantToWallClock,
	type Location,
	LocationSchema,
	wallClockToInstant,
} from "./location.entity.ts";
import {
	DEFAULT_TICKET_TEMPLATE_VARIANT,
	type TicketTemplateVariant,
	TicketTemplateVariantSchema,
} from "./ticket-template-variant.const.ts";
import type { Actor } from "./workspace.entity.ts";

export const Cents = z
	.number()
	.int()
	.min(0)
	.describe("a money amount in integer euro cents — floats never touch money");
// Money a field ASKS to move, as opposed to money a field records having
// moved: a recorded figure is legitimately 0 (a comp's give-back moves seats
// and no cents), but an instruction to move nothing is the absence of the
// instruction, expressed by leaving the field — or the whole selection entry —
// out rather than by writing a zero into it.
export const PositiveCents = Cents.min(1).describe(
	"a money amount in integer euro cents that must actually move — zero is not an instruction",
);
export const Qty = z.number().int().min(1);

// events/{eventId}/ticketTypes/{typeId} — public read; member CRUD
// Excluded: a stored soldOut flag (drift generator — availability derives it from the
// quota doc); a stored discount ladder beyond volumeDiscounts (a second price step is
// a second ticket type, chained via showAfterSoldOut, not a tier nested inside one type);
// field-level nullish on salesWindow or entryWindow (opensAt/closesAt independently
// optional) — the closes-after-opens invariant can't be verified by zod once one side
// lives on the event doc, so each window stays whole-object nullish, set or entirely
// inherited; a stored calendar-day string beside entryWindow — the day a window falls on
// is read off the window itself, in the venue's zone, at render time, and a stored copy
// is a second representation of one fact, free to contradict the window it duplicates.
export const TicketTypeSchema = z.object({
	kind: z
		.enum(["ticket", "addon"])
		.describe(
			"add-ons are ticket-type variants presented as their own catalogue",
		),
	// Bounded because delivery freezes this label onto every one of an order's
	// tickets in the single orderAccess projection document: the per-ticket
	// budget shrinks with the label, and an unbounded one reaches the document
	// ceiling MAX_TICKETS_PER_ORDER's own note sizes — a throw AFTER payment, on
	// an order already captured. 120 is past any label an operator writes and
	// leaves the projection well inside that ceiling for Latin text. Unlike
	// description below, which the schema leaves unbounded and every surface
	// bounds at render: that copy is never frozen per ticket into one document.
	name: z.string().min(1).max(120),
	nameByLocale: LocalizedTextSchema.nullish().describe(
		"the type's label in every UI language the storefront serves, written by the localize trigger from `name` — absent, or absent for the reader's own language, renders `name` itself. Read through resolveLocalizedText (@momo/i18n), never keyed directly. The operator's own typed label stays the source of record and the editor retires this whole map when that label changes, so a pane is never a translation of words the source no longer carries. Delivery freezes the resolved label onto the order's own tickets, so the buyer's ticket email, ticket PDF and both wallet passes read the language they bought in. The invoice and the credit note do not: a VAT document names what was sold in the language the operator sells it in, and it stays that way once issued",
	),
	// Which pane restates the operator's own words, and whether the machine has
	// caught up with their last edit, are answerable nowhere but here: the
	// trigger writes the map behind the save, so no gesture of theirs ever puts
	// a pane in front of them. The label is where it matters most — an
	// eligibility qualifier the English pane drops is a § 312j Abs. 2 BGB
	// disclosure defect on the storefront row.
	nameByLocaleStatus: LocalizedCopyStatusSchema.nullish().describe(
		"where `nameByLocale` stands — a pass the operator asked for, a pass in flight, a pass that wrote and which pane holds their own words, or a pass that produced nothing. Absent is a type no pass has run over, which asserts nothing about the label's provenance rather than claiming the machine wrote it",
	),
	description: z
		.string()
		.nullish()
		.describe(
			"buyer-facing copy describing the type itself, rendered under the type's name in the type's row — the public shop and the staff issuing flow both offer it there — as a single line clamped to the row's width that opens the full text. Optional by design, so every surface that renders it holds its layout unchanged when it is absent, and every surface bounds it at render time rather than the schema bounding what is written — length is a content decision, never a layout one.",
		),
	descriptionByLocale: LocalizedTextSchema.nullish().describe(
		"the short buyer-facing copy in every UI language the storefront serves, written by the localize trigger from `description` — absent, or absent for the reader's own language, renders `description` itself. Read through resolveLocalizedText (@momo/i18n), never keyed directly; the operator's own typed string stays the source of record, and the editor retires this whole map when that string changes",
	),
	// The same two unanswerable questions the label carries, on the copy that
	// runs underneath it.
	descriptionByLocaleStatus: LocalizedCopyStatusSchema.nullish().describe(
		"where `descriptionByLocale` stands — a pass the operator asked for, a pass in flight, a pass that wrote and which pane holds their own words, or a pass that produced nothing. Absent is a type no pass has run over",
	),
	vatRate: z
		.number()
		.min(0)
		.max(100)
		.default(7)
		.describe(
			"the German VAT rate this type sells under — per type because workshops may not qualify for 7%",
		),
	price: Cents,
	capacity: z.number().int().min(0),
	order: z
		.number()
		.int()
		.min(0)
		.default(0)
		.describe(
			"the operator's chosen storefront/admin display position among this event's ticket types — lower sorts first, ties break on name. Defaulted, never nullish: every stored doc, including ones written before this field existed, resolves to a real position rather than an unanswered one",
		),
	showAfterSoldOut: z
		.string()
		.nullish()
		.describe(
			"the predecessor typeId this type reveals behind — hidden on the storefront until the predecessor is no longer purchasable: its availability bucket reads soldOut, its resolved salesWindow.closesAt (resolveSalesWindow) has passed, or it no longer exists. Presentation only, never protection — settle enforces capacity and salesWindow regardless of visibility. A cross-document reference zod cannot validate for cycles or dangling targets; an unresolvable pointer fails OPEN — shown, never silently hidden",
		),
	claimableBy: z
		.enum(["buyer", "staff", "code"])
		.nullish()
		.describe(
			"who may obtain this type — 'buyer' offers it in the public shop, 'staff' hides it there and leaves the guest arm as its only issuance path, 'code' hides it there and reveals it to a checkout carrying a voucher whose unlocks name this type. The one audience field: a second visibility flag beside it would be two representations of one decision, free to disagree. Absent is unanswered rather than 'buyer': resolveClaimableBy below reads an unanswered free type as 'staff', so a free allocation is never publicly claimable until an operator says so. Resolve through that function, never read this field directly",
		),
	verifyAtDoor: z
		.boolean()
		.default(false)
		.describe(
			"whether a person admits this type at the door — true refuses every on-decode admission, so a doorperson confirms by hand before anyone walks in and a decode alone can never let a press guest through. Defaulted rather than nullish because absent is a real answer here: no verification, the ordinary scan-and-go door — unlike claimableBy, whose absence is genuinely unanswered and which the price resolves. Defaulted rather than required because the door and the admin list both read this catalog through a safeParse that skips whatever will not parse: a required key would drop every type already stored without it out of both, and a type the door cannot resolve is a type the door cannot admit",
		),
	doorNote: z
		.string()
		.nullish()
		.describe(
			"the one thing staff is asked to check before admitting — a valid press pass, a crew wristband — authored where the door policy is set and rendered as the whole instruction on the hold card, never beneath a second generic sentence restating it. Distinct from description, which is buyer-facing shop copy: this line is read only by an operator standing at the door, so it names what to look at rather than what the type is",
		),
	volumeDiscounts: z
		.array(z.object({ minQty: Qty, unitPrice: PositiveCents }))
		.default([])
		.describe(
			"reduced unit price at or above a quantity. Positive: a zero tier is not a discount but a re-price of a paid type to free at a quantity the buyer picks, and free is an audience decision claimableBy makes deliberately, never a side effect of basket size",
		),
	salesWindow: z
		.object({ opensAt: z.custom<Timestamp>(), closesAt: z.custom<Timestamp>() })
		.refine((w) => w.closesAt.toMillis() > w.opensAt.toMillis(), {
			message: "sales window must close after it opens",
		})
		.nullish()
		.describe(
			"when this type may be BOUGHT, as against entryWindow below, which says when it ADMITS. Absent inherits — resolve with resolveSalesWindow(type, event) below, never read directly; the resolved value is never persisted back to this field, so a later event.schedule or entryWindow edit keeps propagating to every type still inheriting",
		),
	entryWindow: z
		.object({ opensAt: z.custom<Timestamp>(), closesAt: z.custom<Timestamp>() })
		.refine((w) => w.closesAt.toMillis() > w.opensAt.toMillis(), {
			message: "entry window must close after it opens",
		})
		.nullish()
		.describe(
			"the span during which this type ADMITS someone at the door — the fact salesWindow above does not carry: that window governs only when the type may be BOUGHT, and a type is routinely sold for months before, and outside, the hours it opens the door for. Absent inherits the event’s own schedule, both bounds — resolve with resolveEntryWindow(type, event) below, never read directly; the resolved value is never persisted back to this field, so a later event.schedule edit keeps propagating to every type still inheriting",
		),
	locationId: z
		.string()
		.nullish()
		.describe(
			"an optional override venue or room for this type alone — the concert type is in Saal 1, another in Shedhalle — absent means the event’s own locationId applies. Presentation and artifact-rendering only; settle and its authority checks never read it",
		),
	templateVariant: TicketTemplateVariantSchema.nullish().describe(
		"an override rendering variant for this type alone — absent means the type inherits the event's own templateVariant. Resolve via resolveTicketTemplateVariant below, never read directly",
	),
});
export type TicketType = z.infer<typeof TicketTypeSchema>;
// A writer that omits a .default()-bearing field (vatRate, order,
// verifyAtDoor, volumeDiscounts) and relies on TicketTypeSchema.parse to fill
// it types its own literal against this z.input, not z.infer's output — the
// same literal typed against TicketType would demand those fields the writer
// never sets.
export type TicketTypeInput = z.input<typeof TicketTypeSchema>;

// The unit price a quantity of this type actually buys at: the highest
// volumeDiscounts tier whose minQty the quantity reaches, else the base price.
// The single authority the storefront prices a basket line for display with and
// settle (and preparePayment) re-derive the authoritative charge from — the
// buyer authors a line's typeId and qty, never its price, so a pricedLine's
// unitPrice can only ever be a value this catalog set, and a tampered one is
// re-derived away before any money moves. Pure — the caller multiplies by qty
// for the line amount.
export function resolveUnitPrice(
	type: Pick<TicketType, "price" | "volumeDiscounts">,
	qty: number,
): number {
	const tier = type.volumeDiscounts
		.filter((discount) => qty >= discount.minQty)
		.toSorted((a, b) => b.minQty - a.minQty)[0];
	return tier?.unitPrice ?? type.price;
}

// Who may obtain this type: the operator's own answer when the catalog carries
// one, returned verbatim, else the price decides. An unanswered free type
// resolves "staff" — a €0 allocation is capacity-bearing and anyone with a
// browser can drain it, so the absent value fails safe instead of being
// backfilled: nothing to migrate, no fork left holding the loose reading, and
// an operator's deliberate opt-in is the only thing that puts a free type in
// the public shop. An unanswered priced type resolves "buyer", the ordinary
// shop; "code" is only ever an answer an operator gave, since a price cannot
// imply a voucher. The one function every audience decision reads, so a type
// the shop hides can never be one settle accepts. Pure.
export function resolveClaimableBy(
	type: Pick<TicketType, "price" | "claimableBy">,
): "buyer" | "staff" | "code" {
	return type.claimableBy ?? (type.price === 0 ? "staff" : "buyer");
}

// The window a type actually admits under — the one function every surface asking
// “does this open the door now?” reads, so the shop's day chips, the setup form and
// the door can never independently drift. A present entryWindow returns its own two
// fields untouched. An absent one inherits the event's own schedule, BOTH bounds —
// unlike resolveSalesWindow below, whose absent case resolves opensAt to null: a
// missing lower bound on SALES means presale is open, which is what an unconfigured
// type should do, while a missing lower bound on ENTRY would mean a ticket admits
// before its event exists, which is a hole rather than a feature. Pure — never
// writes back, so a later event.schedule edit keeps reaching every type still
// inheriting; the caller freezes the result once, at fulfillment.
export function resolveEntryWindow(
	type: Pick<TicketType, "entryWindow">,
	event: Pick<Event, "schedule">,
): { opensAt: Timestamp; closesAt: Timestamp } {
	return (
		type.entryWindow ?? {
			opensAt: event.schedule.start,
			closesAt: event.schedule.end,
		}
	);
}

// The effective sales window a type actually sells under — the one function the storefront
// and settle both call, so neither can independently drift from the other (a buyable-looking
// type that settle then refuses is the failure this guards against). A present salesWindow
// returns its own two fields untouched. An absent one resolves opensAt to null — no lower
// bound, immediately purchasable, an inherited window is never “not yet open” — deliberately
// NOT event.schedule.start, which would silently block presale (the majority of ticket
// revenue) for every type an admin leaves unconfigured; closesAt resolves to the moment the
// type stops admitting, since selling entry to a day already over is selling nothing. That
// upper bound is resolveEntryWindow's, not event.schedule.end directly: a type carrying no
// entryWindow resolves to event.schedule.end anyway, so an unconfigured type keeps selling
// until the event is over, and only a type naming its own admitting hours narrows — a
// Saturday ticket stops selling on Saturday rather than the following Wednesday. Pure —
// never writes back.
export function resolveSalesWindow(
	type: Pick<TicketType, "salesWindow" | "entryWindow">,
	event: Pick<Event, "schedule">,
): { opensAt: Timestamp | null; closesAt: Timestamp } {
	if (type.salesWindow) return type.salesWindow;
	return { opensAt: null, closesAt: resolveEntryWindow(type, event).closesAt };
}

// The rendering variant a type's tickets actually mint under — the same
// three-rung fallback shape resolveSalesWindow and resolveEffectiveTimezone
// both already follow: the type's own templateVariant wins when set, the
// event's own templateVariant wins next, and the package's built-in
// DEFAULT_TICKET_TEMPLATE_VARIANT is the terminal fallback. No catalog, no
// FK, no dangling/unpublished case — a stored variant is always a live enum
// member. Pure — never writes back; the caller freezes the result once, at
// fulfillment.
export function resolveTicketTemplateVariant(
	type: Pick<TicketType, "templateVariant">,
	event: Pick<Event, "templateVariant">,
): TicketTemplateVariant {
	return (
		type.templateVariant ??
		event.templateVariant ??
		DEFAULT_TICKET_TEMPLATE_VARIANT
	);
}

// The IANA zone a type's naive wall-clock fields (its own salesWindow, and
// the event's schedule it may inherit) actually resolve against — the same
// FK chain resolveSalesWindow's venue-override shape follows: a type's own
// locationId wins when set (the Saal-1-only workshop), the event's own
// locationId otherwise. `locations` is keyed by locationId, mirroring how
// every caller already holds the venue catalog (a Firestore query result, a
// designer fixture map) rather than one document. An unresolvable locationId
// — a dangling FK, a venue deleted out from under a stale read — falls back
// to LocationSchema's own default rather than throwing, the same forward-only
// posture the schema's `.default()` already commits to: never a hard failure
// on the storefront's render path for a fact this deployment already has one
// sane answer for.
export function resolveEffectiveTimezone(
	type: Pick<TicketType, "locationId">,
	event: Pick<Event, "locationId">,
	locations: Record<string, Pick<Location, "timezone">>,
): string {
	const locationId = type.locationId ?? event.locationId;
	return (
		locations[locationId]?.timezone ??
		LocationSchema.shape.timezone.parse(undefined)
	);
}

// ── The day dimension — derived from entryWindow, never stored ──────────────
// The whole reason the schema above excludes a stored calendar day: one
// derivation, read by the shop's chips, the console's inventory and the setup
// form alike, so no two surfaces can answer "which day is this type for?"
// differently. Every function here is pure and takes epoch millis, the one
// shape every caller already holds (a Timestamp's toMillis(), a resolved
// window, a row's own field) and none passes back to Firestore.

/** One calendar day a catalog admits on, in the venue's own zone. */
export type ProgrammeDay = {
	/** `YYYY-MM-DD` — a comparable, sortable key, never a rendering: the label
	 *  is formatted from it at the surface that shows it. */
	readonly day: string;
	/** That day's own door hours as epoch millis — the earliest opening and
	 *  the latest closing across every window admitting on it. A multi-day
	 *  pass reaching this day contributes its own hours without dragging
	 *  another day's date into the label. */
	readonly opensAt: number;
	readonly closesAt: number;
	/** The zone `day`, `opensAt` and `closesAt` are all read in. */
	readonly timezone: string;
};

/** The calendar day an instant falls on in a given zone, as `YYYY-MM-DD` —
 *  `en-CA` is the locale whose own date format IS that, which is what makes a
 *  day a comparable, sortable key rather than a rendering. */
export function dayKeyOf(millis: number, timezone: string): string {
	return new Date(millis).toLocaleDateString("en-CA", { timeZone: timezone });
}

/** Every calendar day an entry window overlaps, in the venue's own zone — one
 *  day for an ordinary day ticket, three for a three-day pass. BOTH bounds are
 *  inclusive, so a window closing at the first instant of a day still admits
 *  on that day and a window opening at its last instant still does too: the
 *  door is open at that instant, which is the whole question a day asks. */
export function daysCovered(
	window: { opensAt: number; closesAt: number },
	timezone: string,
): readonly string[] {
	const last = dayKeyOf(window.closesAt, timezone);
	// Stepping the DAY KEY, never the instant: adding 24h to an instant drifts
	// an hour across a DST boundary and can repeat or skip a day, while adding
	// one to a UTC date is exact in every zone.
	const cursor = new Date(
		`${dayKeyOf(window.opensAt, timezone)}T00:00:00.000Z`,
	);
	const days: string[] = [];
	for (
		let key = cursor.toISOString().slice(0, 10);
		key <= last;
		key = cursor.toISOString().slice(0, 10)
	) {
		days.push(key);
		cursor.setUTCDate(cursor.getUTCDate() + 1);
	}
	return days;
}

/** A window re-anchored so its FIRST day is `day`, in `timezone`: both ends
 *  keep their naive wall clock and the span between them is preserved, so a
 *  three-day pass dropped on D runs D..D+2 rather than collapsing to one day.
 *  Null — never a throw — when either end is not a real time on its new day in
 *  that zone, which is the spring-forward hour that does not exist there. */
export function reanchorWindowToDay(
	window: { opensAt: Date; closesAt: Date },
	day: string,
	timezone: string,
): { opensAt: Date; closesAt: Date } | null {
	const openNaive = instantToWallClock(window.opensAt, timezone);
	const closeNaive = instantToWallClock(window.closesAt, timezone);
	// Stepping the DAY KEY by the span the source covers, never adding the
	// millisecond difference to an instant: the same reason daysCovered above
	// walks keys — 24h across a DST boundary repeats or skips a day.
	const spanDays =
		daysCovered(
			{
				opensAt: window.opensAt.getTime(),
				closesAt: window.closesAt.getTime(),
			},
			timezone,
		).length - 1;
	const closeDay = new Date(`${day}T00:00:00.000Z`);
	closeDay.setUTCDate(closeDay.getUTCDate() + spanDays);
	const opensAt = wallClockToInstant(`${day}T${openNaive.slice(11)}`, timezone);
	const closesAt = wallClockToInstant(
		`${closeDay.toISOString().slice(0, 10)}T${closeNaive.slice(11)}`,
		timezone,
	);
	if ("error" in opensAt || "error" in closesAt) return null;
	return { opensAt, closesAt };
}

/** A window filed under `day` alone, in `timezone`: both ends keep their naive
 *  wall clock and the span between them is discarded, because a type with no
 *  day opinion of its own names hours, not days — the multi-day span it reads
 *  as is the event's schedule it inherited, not an offer running that long.
 *  The close rolls to the following day only when its wall clock is at or
 *  before the open's, which is the night that runs past midnight. Null — never
 *  a throw — when either end is not a real time on its new day in that zone,
 *  which is the spring-forward hour that does not exist there. */
export function collapseWindowToDay(
	window: { opensAt: Date; closesAt: Date },
	day: string,
	timezone: string,
): { opensAt: Date; closesAt: Date } | null {
	const openNaive = instantToWallClock(window.opensAt, timezone);
	const closeNaive = instantToWallClock(window.closesAt, timezone);
	// Stepping the DAY KEY for the midnight roll, never adding 24h to an
	// instant: the same reason daysCovered above walks keys.
	const closeDay = new Date(`${day}T00:00:00.000Z`);
	if (closeNaive.slice(11) <= openNaive.slice(11))
		closeDay.setUTCDate(closeDay.getUTCDate() + 1);
	const opensAt = wallClockToInstant(`${day}T${openNaive.slice(11)}`, timezone);
	const closesAt = wallClockToInstant(
		`${closeDay.toISOString().slice(0, 10)}T${closeNaive.slice(11)}`,
		timezone,
	);
	if ("error" in opensAt || "error" in closesAt) return null;
	return { opensAt, closesAt };
}

/** Whether a window spans the event's whole schedule — which is exactly what
 *  an absent entryWindow inherits, and equally what an operator who set those
 *  bounds by hand meant: the same offer, so both must be read the same way
 *  whichever way they were authored. Such a type is tied to no day and
 *  therefore contributes none: a whole-festival pass would otherwise mint one
 *  day per calendar day it covers, each leading back to the same single row. */
export function coversWholeEvent(
	window: { opensAt: number; closesAt: number },
	event: Pick<Event, "schedule">,
): boolean {
	return (
		window.opensAt <= event.schedule.start.toMillis() &&
		window.closesAt >= event.schedule.end.toMillis()
	);
}

/** The day dimension of a catalog: every calendar day at least one of the
 *  given windows opens the door on, each carrying that day's own widest door
 *  hours, sorted. Callers pass only their day-bound windows — a whole-event
 *  one (coversWholeEvent above) has no opinion about days — so a catalog
 *  where nobody set an entry window at all yields no days, and the surface
 *  above renders exactly what it rendered before the day dimension existed. */
export function programmeDaysOf(
	windows: readonly { opensAt: number; closesAt: number; timezone: string }[],
): readonly ProgrammeDay[] {
	const byDay = new Map<string, ProgrammeDay>();
	for (const window of windows) {
		for (const day of daysCovered(window, window.timezone)) {
			const seen = byDay.get(day);
			byDay.set(day, {
				day,
				timezone: window.timezone,
				opensAt: Math.min(seen?.opensAt ?? Infinity, window.opensAt),
				closesAt: Math.max(seen?.closesAt ?? -Infinity, window.closesAt),
			});
		}
	}
	return [...byDay.values()].sort((a, b) => a.day.localeCompare(b.day));
}

/** Which day a surface is showing: the person's own pick while it still names
 *  a day, else the first day whose door has not already shut, else the last
 *  day there is. A pick that stops naming a day — its window closed while the
 *  page stood open, the catalog reloaded, the operator moved to another event
 *  — heals itself instead of stranding the list empty, and a surface that
 *  keeps past days (the console's record of what sold) still lands on the one
 *  that is live rather than on the first morning of the run. Null exactly when
 *  there are no days at all. */
export function resolveProgrammeDay(
	days: readonly ProgrammeDay[],
	pick: string | null,
	now: number,
): string | null {
	return (
		days.find((d) => d.day === pick)?.day ??
		days.find((d) => d.closesAt >= now)?.day ??
		days.at(-1)?.day ??
		null
	);
}

// ── What a paid advertisement may say about this catalog ───────────────────
// TWO QUESTIONS, TWO ANSWERS, AND THEY ARE NOT THE SAME SET. What an ad may
// quote a from-price from is what a stranger can buy AT ONE MOMENT; what
// bounds the run an ad is paid for is the last close over what the public may
// buy AT ALL. A tier opening next week is excluded from the price and is
// still sold during the run, so one set answering both quotes a price nobody
// can pay or ends a campaign while its own shop is still open. The two rules
// neither question asks a moment about — an ordinary ticket, claimable by the
// public — are declared once in `publicTicketTypes` below; the four that read
// the moment's own clock and inventory are declared once in
// `advertisableTicketTypes`.
//
// INVENTORY BOUNDS THE PRICE AND NEVER THE RUN. A sold-out tier quotes no
// price, because the money cannot move through it today. It still bounds the
// run, because a refund reopens it and the campaign is still running when
// that happens — the same reason a tier weeks from opening bounds the run.
// The pause that answers an event whose every advertised tier has gone is
// `sync.schedule.ts`'s own watch, on live inventory, reversible in a tick;
// an `endsAt` derived from inventory would be a stop nothing reverses.
//
// Every campaign surface reads these: the prompt the copy is composed from,
// the confirmation that refuses a launch whose floor has moved, the console's
// offer and staleness reads, and the studio's own verdict panel. None of them
// reads `kind`, `claimableBy`, `salesWindow`, `showAfterSoldOut`, the
// availability bucket or `price` a second time, which is what makes an
// advertised price a price the shop actually charges.

/**
 * The types this event's paid advertising sells, whenever they sell: an
 * ordinary ticket the public may buy.
 *
 * `kind` is `ticket` because an add-on is a variant catalog beside the shop
 * rather than a way into it — a €3 cloakroom item undercutting every ticket
 * would have the ad quote the cloakroom as the price of getting in, and would
 * keep a run alive past the last ticket anyone can buy. It is the one
 * exclusion the audience rule cannot make: an add-on genuinely IS
 * buyer-claimable.
 *
 * `resolveClaimableBy` reads `buyer` because a `staff` type is an allocation
 * nobody with a browser may claim and a `code` type is revealed only to a
 * checkout already carrying the voucher — neither is on public sale however
 * low its price, and a paid ad pointing at either sends money to a door that
 * cannot take it.
 */
function publicTicketTypes<
	T extends Pick<TicketType, "kind" | "price" | "claimableBy">,
>(ticketTypes: readonly T[]): T[] {
	return ticketTypes.filter(
		(type) => type.kind === "ticket" && resolveClaimableBy(type) === "buyer",
	);
}

/**
 * Whether the money can move through this type at `atMillis` — its resolved
 * salesWindow is open and its bucket has not sold out. A bucket with no entry
 * has never settled or been refused, which is not sold out.
 *
 * Both bounds are closed, which is the settle transaction's own reading of the
 * same window (settle.webhook.ts): a line landing at exactly `opensAt` or at
 * exactly `closesAt` is admitted, so the instant this answers false for is an
 * instant settle refuses. `resolvePublicTicketSalesEnd` below reads the same
 * close as the last instant the public can still buy, and a run bounded there
 * would otherwise outlive the price floor by a millisecond.
 */
function purchasableAt(
	type: Pick<TicketType, "salesWindow" | "entryWindow"> & {
		readonly id: string;
	},
	event: Pick<Event, "schedule">,
	availability: Availability,
	atMillis: number,
): boolean {
	const window = resolveSalesWindow(type, event);
	if (window.opensAt !== null && window.opensAt.toMillis() > atMillis)
		return false;
	if (window.closesAt.toMillis() < atMillis) return false;
	return availability[type.id]?.state !== "soldOut";
}

/**
 * Whether the shop puts this type on the page at `atMillis`. An unchained type
 * always is; a chained one waits until its named predecessor can no longer be
 * bought, and a pointer naming a type this catalog does not hold fails OPEN —
 * shown, never silently hidden, the reading `showAfterSoldOut` itself states.
 *
 * The predecessor is resolved against the WHOLE catalog, never the public
 * subset: a type gated behind a staff-only or add-on predecessor would stop
 * resolving it and reveal itself, which is the opposite of what its gate says.
 */
function revealedAt<
	T extends Pick<
		TicketType,
		"salesWindow" | "entryWindow" | "showAfterSoldOut"
	> & { readonly id: string },
>(
	type: T,
	ticketTypes: readonly T[],
	event: Pick<Event, "schedule">,
	availability: Availability,
	atMillis: number,
): boolean {
	if (!type.showAfterSoldOut) return true;
	const predecessor = ticketTypes.find((p) => p.id === type.showAfterSoldOut);
	if (!predecessor) return true;
	return !purchasableAt(predecessor, event, availability, atMillis);
}

/**
 * Those of them a stranger can buy at `atMillis` — the types a from-price may
 * be quoted from. Four further rules, each individually decisive against a
 * real catalog: a presale tier that opens next week is not a price a click
 * today can pay, a tier that stopped selling is not a price at all, a tier
 * whose bucket has sold out is a row the shop still renders and no money moves
 * through, and a tier chained behind a predecessor still on sale is not on the
 * page at all — an ad quoting it sends a stranger to a shop where the row it
 * named does not exist.
 *
 * The two inventory-reading rules are one fact: `purchasableAt` is what the
 * chain waits on and what the quoted price needs, so a first tier selling out
 * withdraws its own price and reveals its successor's in the same read.
 *
 * `availability` rides on the event document beside `schedule` — one public
 * field, one listener — so the `Pick` costs no caller a second read.
 *
 * Pure, and reading the instant from its caller rather than the clock, so the
 * floor a proposal's words are composed against is the floor the confirmation
 * later re-derives and compares (`campaign.entity.ts`'s `priceFloorCents`).
 * Inventory is live rather than pinned by that instant, so a tier selling out
 * between composition and launch moves the floor and the confirmation refuses
 * — the ad's own words having gone wrong, which is what it exists to catch.
 */
export function advertisableTicketTypes<
	T extends Pick<
		TicketType,
		| "kind"
		| "price"
		| "claimableBy"
		| "salesWindow"
		| "entryWindow"
		| "showAfterSoldOut"
	> & { readonly id: string },
>(
	ticketTypes: readonly T[],
	event: Pick<Event, "schedule" | "availability">,
	atMillis: number,
): T[] {
	const availability = event.availability ?? {};
	return publicTicketTypes(ticketTypes).filter(
		(type) =>
			purchasableAt(type, event, availability, atMillis) &&
			revealedAt(type, ticketTypes, event, availability, atMillis),
	);
}

/**
 * The cheapest a single ticket to this event can be bought for at `atMillis`,
 * in euro cents — the figure ad copy splices as literal text, and the figure
 * a parked proposal is measured against.
 *
 * Quantity one, because an ad's "from" price is what ONE person pays: a
 * volume tier reachable only at ten tickets is not a price a stranger
 * scrolling can act on. `resolveUnitPrice` rather than `price` directly,
 * because `Qty` starts at 1 — a tier whose `minQty` is 1 IS the price one
 * person pays, and reading the base price past it advertises an amount the
 * shop does not charge.
 *
 * `null` when nothing is advertisable, which is an event no honest ad exists
 * for rather than one advertised without a price.
 */
export function resolvePriceFloor(
	ticketTypes: readonly (Pick<
		TicketType,
		| "kind"
		| "price"
		| "claimableBy"
		| "salesWindow"
		| "entryWindow"
		| "volumeDiscounts"
		| "showAfterSoldOut"
	> & { readonly id: string })[],
	event: Pick<Event, "schedule" | "availability">,
	atMillis: number,
): number | null {
	const prices = advertisableTicketTypes(ticketTypes, event, atMillis).map(
		(type) => resolveUnitPrice(type, 1),
	);
	return prices.length === 0 ? null : Math.min(...prices);
}

/**
 * The last instant the public can still buy a ticket to this event, in epoch
 * millis — `campaign.entity.ts`'s `endsAt`, and where a paid run stops: a day
 * of advertising past it sends people to a shop that cannot take their money.
 *
 * No instant is read, deliberately. A tier still weeks from opening bounds
 * the run exactly as a tier on sale now does, because the campaign is still
 * running when it opens — measuring this against the advertisable set would
 * end the campaign early on every catalog whose last tier has not opened yet.
 *
 * `null` when the public may buy no ticket at all, which is an event with no
 * public shop rather than one whose shop has closed.
 */
export function resolvePublicTicketSalesEnd(
	ticketTypes: readonly Pick<
		TicketType,
		"kind" | "price" | "claimableBy" | "salesWindow" | "entryWindow"
	>[],
	event: Pick<Event, "schedule">,
): number | null {
	const closes = publicTicketTypes(ticketTypes).map((type) =>
		resolveSalesWindow(type, event).closesAt.toMillis(),
	);
	return closes.length === 0 ? null : Math.max(...closes);
}

export const TicketType = entity<Actor>()({
	schema: TicketTypeSchema,
	actions: ["read", "list", "create", "update", "delete"] as const,
})
	.pipe(({ can, when, hasPermission, and, pathVar }) => [
		// Public: the storefront browses the catalog with no auth.
		can("read").when(() => and()),
		can("list").when(() => and()),
		// Member CRUD: mirrors Event's manage gate — denied until mfa:true. The
		// admin form (not this rule) refuses showAfterSoldOut cycles/self-
		// reference and warns on a descending chained price.
		when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.events.manage", {
					event: pathVar("eventId"),
				}),
				me.mfa.eq(true),
			),
		).allow("create", "update"),
		// Same authority, no emitted allow line — see the file-level note.
		can("delete")
			.when((_data, me) =>
				and(
					hasPermission(me.permissions, "tickets.events.manage", {
						event: pathVar("eventId"),
					}),
					me.mfa.eq(true),
				),
			)
			.requiresHandler("deleteTicketType"),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/events/{eventId}/ticketTypes/{typeId}",
		}),
	);
